import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Stack } from 'react-bootstrap';
import { asve } from '../orve';
import {
  getAssessmentContentPercentile,
  getAssessmentContentBlank,
  getAssessmentText,
  getCandidateLeaderText,
  valuesRatingBreakpoints,
  getCandidateValuesText,
  updateValuesOverallText,
} from '../report-util';
import { useAuth } from '../../../../providers/auth-provider';
import { STAFFING_ONSITE_OPENING, STAFFING_REMOTE_OPENING } from '../../../shared/filters/filter-common';
import { REPORT_TYPE } from '../../../../utils/constants';
import { getValuesBreakpoints, getValuesSummary, getValuesTitle } from '../assessment-verbiage';

function ValuesFitResults({ candidate, remoteStaffing, reportType }) {
  const { isSelect } = useAuth();
  const isStaffing = reportType === REPORT_TYPE.staffing;
  const [assessmentText, setAssessmentText] = useState({});
  const [valuesAttribute, setValuesAttribute] = useState('values');

  const vText = [
    `To help you understand the new hire’s desired work environment – the type of culture that will lead to high performance and loyalty – we have provided a list of the most important work values.`,
    `To help you motivate and manage your new employee, below is a list of Work Values that are the most important drivers of job performance and loyalty.`,
  ];

  const displayAlignedValues = useCallback(() => {
    if (reportType === REPORT_TYPE.staffing) {
      return false;
    }
    return candidate[valuesAttribute]?.aligned?.length > 0;
  }, [candidate]);

  const displayMisAlignedValues = useCallback(() => {
    if (reportType === REPORT_TYPE.staffing) {
      return false;
    }
    return candidate[valuesAttribute]?.misAligned?.length > 0;
  }, [candidate]);

  useEffect(() => {
    if (candidate) {
      setAssessmentText(asve(candidate));
      if (isStaffing) {
        if (remoteStaffing) {
          setValuesAttribute(`${STAFFING_REMOTE_OPENING}Values`);
        } else {
          setValuesAttribute(`${STAFFING_ONSITE_OPENING}Values`);
        }
      }
    }
  }, [candidate]);

  return (
    <Container className="bg-white mt-2 mb-3 candidate-profile-assessments">
      {candidate && (
        <>
          <Container className="candidate-profile-assessment-content">
            <Row className="w-50 justify-content-left">
              <h3 className="pt-4 pb-1">{getCandidateValuesText(isSelect()).toUpperCase()}</h3>
              {!isStaffing && (
                <Stack direction="horizontal" gap={4}>
                  {getAssessmentContentPercentile(
                    `Overall ${getCandidateValuesText(isSelect(), true)} Fit`,
                    candidate.percentiles.values,
                    reportType
                  )}
                </Stack>
              )}
            </Row>
            <Row className="w-100 pb-2">
              {getAssessmentText(isStaffing ? vText : updateValuesOverallText(isSelect(), assessmentText.values))}
            </Row>
            {displayMisAlignedValues() && (
              <>
                <Row className="w-100 pb-2">
                  <div className="pt-4">
                    Below is a list of{` ${getCandidateValuesText(isSelect(), true)} `}the
                    {` ${getCandidateLeaderText(isSelect())} `}indicated were more important than the Organization
                    currently provides (i.e., the {` ${getCandidateValuesText(isSelect(), true)} `}with the greatest
                    misalignment).
                  </div>
                </Row>
                <Row className="w-100 mx-auto justify-content-center pt-3">
                  {candidate[valuesAttribute]?.misAligned.slice(0, 4).map((key) => {
                    return (
                      <Stack direction="horizontal" className="subscore-chart" key={key}>
                        <div className="w-25">
                          {getAssessmentContentBlank(
                            candidate[valuesAttribute].titles[key],
                            candidate[valuesAttribute].ratings[key],
                            valuesRatingBreakpoints,
                            reportType
                          )}
                        </div>
                        <div className="w-75">{getAssessmentText(assessmentText[key])}</div>
                      </Stack>
                    );
                  })}
                </Row>
              </>
            )}
            {displayAlignedValues() && (
              <>
                <Row className="w-100 mx-auto pt-4 pb-2">
                  Below is a list of{` ${getCandidateValuesText(isSelect(), true)} `}where there is strong alignment
                  between the
                  {` ${getCandidateLeaderText(isSelect())} `} and the Organization (i.e., the
                  {` ${getCandidateValuesText(isSelect(), true)} `}with the strongest alignment).
                </Row>
                <Row className="w-100 mx-auto justify-content-center pt-3">
                  {candidate[valuesAttribute]?.aligned.slice(0, 4).map((key) => {
                    return (
                      <Stack direction="horizontal" className="subscore-chart" key={key}>
                        <div className="w-25">
                          {getAssessmentContentBlank(
                            candidate[valuesAttribute].titles[key],
                            candidate[valuesAttribute].ratings[key],
                            valuesRatingBreakpoints,
                            reportType
                          )}
                        </div>
                        <div className="w-75">{getAssessmentText(assessmentText[key])}</div>
                      </Stack>
                    );
                  })}
                </Row>
              </>
            )}
            {isStaffing && (
              <Row className="w-100 mx-auto justify-content-center pt-3">
                <>
                  {candidate.values.variableKeys.map((key) => {
                    const title = getValuesTitle(candidate, key);
                    if (title) {
                      return (
                        <Stack key={key} direction="horizontal" className="subscore-chart">
                          <div className="w-25">
                            {getAssessmentContentBlank(
                              title,
                              candidate.values.scores[key],
                              getValuesBreakpoints(candidate),
                              reportType
                            )}
                          </div>
                          <div className="w-75">{getValuesSummary(candidate, key)}</div>
                        </Stack>
                      );
                    }
                  })}
                </>
              </Row>
            )}
          </Container>
        </>
      )}
    </Container>
  );
}

export default ValuesFitResults;
